import React, { useState, useRef, useEffect } from "react";
import styles from "./Menu.module.scss";
import MenuThemes, { MenuTheme } from "./MenuThemes";
import classnames from "classnames";
import useClickedOutside from "@src/hooks/useClickedOutside";
import MenuAlignments, { MenuAlignment } from "./MenuAlignments";
import map from "lodash/map";

interface MenuController {
  setIsOpen: (isOpen: boolean) => void;
}

interface MenuVariations {
  isSidebarShowing?: boolean;
  noCarat?: boolean;
  dark?: boolean;
}

interface MenuProps {
  buttonContent?: React.ReactNode;
  isButtonContentInput?: boolean;
  children?: React.ReactNode;
  theme?: MenuTheme;
  align?: MenuAlignment;
  onReceiveController?: (controller: MenuController) => void;
  onOpenChanged?: (isOpen: boolean) => void;
  variations?: MenuVariations;
  "data-testid"?: string;
}

export default function Menu({
  buttonContent,
  isButtonContentInput,
  children,
  theme = MenuThemes.Default,
  align = MenuAlignments.Right,
  onReceiveController,
  onOpenChanged,
  variations = {},
  "data-testid": dataTestId = "menu",
}: MenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickedOutside(rootRef, () => setIsOpen(false));

  useEffect(() => {
    onReceiveController?.({
      setIsOpen: (isOpen: boolean) => setIsOpen(isOpen),
    });
  }, [onReceiveController]);

  useEffect(() => {
    onOpenChanged?.(isOpen);
  }, [isOpen, onOpenChanged]);

  const handleToggleMenuOpened = () => {
    if (isButtonContentInput) {
      if (!isOpen) {
        setIsOpen(!isOpen);
      }
    } else {
      setIsOpen(!isOpen);
    }
  };

  const variationStyles = map(variations, (v, k) =>
    v ? styles[k] : null,
  ).filter((v) => !!v);
  return (
    <div
      className={classnames(styles.root, styles[theme], ...variationStyles, {
        [styles.open]: isOpen,
      })}
      ref={rootRef}
      data-testid={`${dataTestId}-root`}
    >
      {!!buttonContent && (
        <div
          className={styles.toggleMenuButton}
          onClick={handleToggleMenuOpened}
          data-testid={`${dataTestId}-toggle-button`}
        >
          {buttonContent}
        </div>
      )}

      {isOpen && (
        <div
          className={classnames(styles.menuContainer, styles[align])}
          data-testid={`${dataTestId}-dropdown-container`}
        >
          {children}
        </div>
      )}
    </div>
  );
}
